import { LuHourglass, LuTimer } from "react-icons/lu";
import { AiOutlineDollar } from "react-icons/ai";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FaLocationDot } from "react-icons/fa6";
import { FaCheck, FaTimes } from "react-icons/fa";
import apiRequests from "../../Component/axios/config/config";

export default function OrderDetails() {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState("");
  const [checkedSteps, setCheckedSteps] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);

  const steps = ["Confirming", "Order ready", "Out for delivery", "Delivered"];

  const apiStatusMap = {
    Confirming: "accepted",
    Delivered: "complete",
  };

  const modalConfig = {
    Confirming: {
      title: "Confirming order?",
      text: "This will notify the customer that the products are exist and you are ready to prepair the order",
      confirmLabel: "Confirm",
    },
    "Order ready": {
      title: "Mark Order as Ready?",
      text: "This will notify the delivery partner that the order is ready for pickup. Please make sure the order is fully prepared and packaged before confirming.",
      confirmLabel: "Mark ready",
    },
    "Out for delivery": {
      title: "Mark order as out for delivery",
      text: "This will notify the customer that delivery partner has picked up the order and will be delevered in a few minutes!",
      confirmLabel: "Mark as on delivery",
    },
    Delivered: {
      title: "Mark order as Delivered?",
      text: "This will notify the customer that the order has been delivered successfully.",
      confirmLabel: "Confirm",
    },
  };

  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "confirmed": return "bg-blue-200 text-blue-700";
      case "on delivery": return "bg-yellow-200 text-yellow-700";
      case "delivered": return "bg-green-200 text-green-700";
      case "denied": return "bg-red-200 text-red-700";
      case "pending": return "bg-orange-100 text-orange-700";
      default: return "bg-gray-200 text-gray-700";
    }
  };

  const isStepEnabled = (step) => {
    const stepIndex = steps.indexOf(step);
    if (stepIndex === 0) return true;
    return checkedSteps.includes(steps[stepIndex - 1]);
  };

  const handleOpenModal = (step) => {
    if (!isStepEnabled(step) || checkedSteps.includes(step)) return;
    setPendingStatus(step);
    setShowModal(true);
  };

  const handleConfirmModal = async () => {
    const apiStatus = apiStatusMap[pendingStatus];

    if (apiStatus) {
      try {
        setApiLoading(true);
        await apiRequests.post(`/invoice/change-status-seller/${id}`, {
          status: apiStatus,
        });
      } catch (error) {
        console.error("Status update failed:", error);
        setApiLoading(false);
        setShowModal(false);
        return;
      } finally {
        setApiLoading(false);
      }
    }

    setCheckedSteps((prev) => [...prev, pendingStatus]);
    setShowModal(false);
  };

  useEffect(() => {
    const fetchOrder = async () => {
      setLoading(true);
      try {
        const res = await apiRequests.get(`/invoice/get-single-seller/${id}`);
        setOrder(res.data.data);
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchOrder();
  }, [id]);

  if (loading) return <div>Loading...</div>;
  if (!order) return <div>No order found.</div>;

  const currentModal = modalConfig[pendingStatus];

  return (
    <div className="flex flex-col gap-8 p-10 font-inter">
      <div className="flex justify-between md:grid md:grid-cols-2 md:gap-8">
        <div className="flex items-center gap-3">
          <LuTimer />
          <p>{new Date(order.createdAt).toLocaleString()}</p>
        </div>
        <div className="flex items-center gap-3">
          <AiOutlineDollar />
          <p>Payment Gateway: <span className="font-bold">Zarin Pall</span></p>
        </div>
        <div className="flex items-center gap-3">
          <AiOutlineDollar />
          <p>Total: {order.totalAmount}$</p>
        </div>
        <div className={`p-4 text-center h-12 rounded-2xl flex items-center justify-center ${getStatusStyle(order.status)}`}>
          {order.status === "pending" && <LuHourglass className="text-xl text-[#937B6C] mr-2" />}
          {order.status}
        </div>
      </div>

      <div className="flex gap-4 items-center border-b py-4">
        <FaLocationDot />
        <p>{order.user_info?.address || "No address"}</p>
      </div>

      <div className="max-h-full overflow-y-auto">
        {order.product_info?.length > 0 ? (
          order.product_info.map((item, index) => (
            <div key={index} className="grid grid-cols-8 md:grid-cols-4 items-center gap-8 py-4 border-b">
              <div className="col-span-1">
                <img
                  src={item.product?.images?.[0]?.path ? `http://localhost:5000${item.product.images[0].path}` : "http://via.placeholder.com/100"}
                  alt={item.name || "Product"}
                  className="rounded-full w-28 h-28 object-cover"
                />
              </div>
              <div className="col-span-7 flex justify-between w-full items-start text-[#937B6C] md:flex-col md:col-span-3 md:gap-4">
                <div className="flex flex-col gap-2">
                  <p className="font-bold text-2xl flex items-center gap-1">
                    {item.name || "Unnamed"} <FaTimes /> {item.quantity || 0}
                  </p>
                  <p className="text-lg">{item.description || "No description"}</p>
                </div>
                <div className="text-xl flex items-center gap-3">
                  <p>Total:</p>
                  <p className="text-green-800 font-bold">{item.price * item.quantity || 0}$</p>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p>No products found.</p>
        )}
      </div>

      <div className="grid grid-cols-4 gap-10 items-center md:grid-cols-1">
        {steps.map((step) => {
          const enabled = isStepEnabled(step);
          const checked = checkedSteps.includes(step);

          return (
            <button
              key={step}
              type="button"
              onClick={() => handleOpenModal(step)}
              disabled={!enabled || checked}
              className={`flex items-center gap-2 p-4 rounded-xl text-left transition-opacity ${
                checked
                  ? "bg-[#051F33] text-white cursor-default"
                  : enabled
                  ? "bg-[#F0E8E4] hover:bg-[#e0d4ce] cursor-pointer"
                  : "bg-[#F0E8E4] opacity-70 cursor-not-allowed"
              }`}
            >
              <div className="min-w-6 min-h-6 border flex items-center justify-center border-black bg-white rounded-full">
                {checked && <FaCheck size={14} className="text-black" />}
              </div>
              <p>{step}</p>
            </button>
          );
        })}
      </div>

      {showModal && currentModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white rounded-2xl shadow-xl flex flex-col gap-6 w-[450px] p-6">
            <h2 className="text-center text-[#2196F3] font-semibold text-lg">
              {currentModal.title}
            </h2>
            <p className="text-center text-gray-700 text-sm leading-relaxed">
              {currentModal.text}
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleConfirmModal}
                disabled={apiLoading}
                className="flex-1 py-2 bg-[#4CAF50] hover:bg-green-700 rounded-lg transition-colors disabled:opacity-60"
              >
                {apiLoading ? "Loading..." : currentModal.confirmLabel}
              </button>
              <button
                onClick={() => setShowModal(false)}
                disabled={apiLoading}
                className="flex-1 py-2 border-2 border-red-400 text-red-400 hover:bg-red-50 font-medium rounded-lg transition-colors disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}